import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Makes an overlay behave like a dialog: moves focus in, traps Tab inside,
 * closes on Escape, and restores focus to the opener when it unmounts.
 * Usage: const ref = useDialog(isOpen, onClose); <div ref={ref} role="dialog" aria-modal="true">
 */
export default function useDialog(isOpen, onClose) {
    const ref = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!isOpen) return undefined;
        const container = ref.current;
        if (!container) return undefined;
        const opener = document.activeElement;
        const focusables = () => Array.from(container.querySelectorAll(FOCUSABLE))
            .filter(el => el.offsetParent !== null || el === document.activeElement);

        const first = container.querySelector('[data-autofocus]') || focusables()[0] || container;
        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
        const t = setTimeout(() => first.focus({ preventScroll: true }), 30);

        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onCloseRef.current?.();
                return;
            }
            if (e.key !== 'Tab') return;
            const list = focusables();
            if (list.length === 0) { e.preventDefault(); return; }
            const idx = list.indexOf(document.activeElement);
            if (e.shiftKey && (idx <= 0)) { e.preventDefault(); list[list.length - 1].focus(); }
            else if (!e.shiftKey && (idx === -1 || idx === list.length - 1)) { e.preventDefault(); list[0].focus(); }
        };
        container.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(t);
            container.removeEventListener('keydown', onKey);
            if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
                opener.focus({ preventScroll: true });
            }
        };
    }, [isOpen]);

    return ref;
}
